import React, { useState } from 'react';
import { formatPhoneDisplay, formatPhoneE164 } from '../utils/phoneUtils';
import './RoomJoinModal.css';

function RoomJoinModal({ 
  isOpen, 
  onClose, 
  onJoinWeb, 
  onJoinPhone, 
  roomName, 
  phoneNumber 
}) {
  const [isRequestingCallback, setIsRequestingCallback] = useState(false);
  const [callbackError, setCallbackError] = useState('');
  const [callbackSuccess, setCallbackSuccess] = useState(false);
  
  // Check if there's an active callback for this room
  const activeCallbackKey = `activeCallback_${roomName}`;
  const CALLBACK_EXPIRY_MINUTES = 10; // Callbacks expire after 10 minutes
  
  const checkActiveCallback = () => {
    const callbackData = localStorage.getItem(activeCallbackKey);
    if (!callbackData) return false;
    
    try {
      const { timestamp } = JSON.parse(callbackData);
      const now = Date.now();
      const expiryTime = CALLBACK_EXPIRY_MINUTES * 60 * 1000; // Convert to milliseconds
      
      if (now - timestamp > expiryTime) {
        // Callback has expired, remove it
        localStorage.removeItem(activeCallbackKey);
        return false;
      }
      
      return true;
    } catch (e) {
      // Invalid data, remove it
      localStorage.removeItem(activeCallbackKey);
      return false;
    }
  };
  
  const hasActiveCallback = checkActiveCallback();

  // Reset success state when modal opens/closes or room changes
  React.useEffect(() => {
    if (!isOpen) {
      // Reset states when modal closes
      setCallbackSuccess(false);
      setCallbackError('');
      setIsRequestingCallback(false);
    }
  }, [isOpen, roomName]);
  
  // Clean up expired callbacks on mount
  React.useEffect(() => {
    const cleanupExpiredCallbacks = () => {
      const keys = Object.keys(localStorage).filter(key => key.startsWith('activeCallback_'));
      const now = Date.now();
      const expiryTime = CALLBACK_EXPIRY_MINUTES * 60 * 1000;
      
      keys.forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.timestamp && (now - data.timestamp > expiryTime)) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // Invalid data, remove it
          localStorage.removeItem(key);
        }
      });
    };
    
    cleanupExpiredCallbacks();
  }, []);

  if (!isOpen) return null;

  const handlePhoneCallback = async () => {
    setIsRequestingCallback(true);
    setCallbackError('');
    setCallbackSuccess(false);

    try {
      // Format phone number to E.164 format
      const formattedPhone = formatPhoneE164(phoneNumber);
      
      if (!formattedPhone) {
        setCallbackError('Invalid phone number format');
        setIsRequestingCallback(false);
        return;
      }
      
      const response = await fetch('https://staging-ai-twilio.myjava.ai/dial-out', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          roomName: roomName,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to initiate callback';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // Don't try to read response again if JSON parsing failed
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Callback initiated:', result);
      
      setCallbackSuccess(true);
      
      // Store that we have an active callback for this room with timestamp
      localStorage.setItem(activeCallbackKey, JSON.stringify({
        timestamp: Date.now(),
        phoneNumber: formattedPhone
      }));
      
      // Don't auto-close - let user close manually
      
    } catch (error) {
      console.error('Error initiating callback:', error);
      setCallbackError(error.message);
    } finally {
      setIsRequestingCallback(false);
    }
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Join "{roomName}"</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {!callbackSuccess ? (
            <>
              <p className="modal-description">
                {hasActiveCallback 
                  ? "You have an active callback for this room. Choose how to rejoin:"
                  : "How would you like to join this room?"}
              </p>
              
              <div className="join-options">
                <button 
                  className="join-option-btn web-join"
                  onClick={onJoinWeb}
                >
                  <i className="fas fa-desktop"></i>
                  <span className="option-title">Join via Web</span>
                  <span className="option-description">
                    Join using your browser with microphone
                  </span>
                </button>
                
                <button 
                  className="join-option-btn phone-join"
                  onClick={handlePhoneCallback}
                  disabled={isRequestingCallback}
                >
                  <i className="fas fa-phone"></i>
                  <span className="option-title">
                    {hasActiveCallback ? "Request Another Callback" : "Request Callback"}
                  </span>
                  <span className="option-description">
                    {hasActiveCallback 
                      ? `Rejoin via phone at ${formatPhoneDisplay(phoneNumber)}`
                      : `We'll call you at ${formatPhoneDisplay(phoneNumber)}`}
                  </span>
                  {isRequestingCallback && (
                    <span className="loading-spinner">
                      <i className="fas fa-spinner fa-spin"></i>
                    </span>
                  )}
                </button>
              </div>
              
              {hasActiveCallback && (
                <div className="info-message" style={{marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e3f2fd', borderRadius: '4px', color: '#1976d2'}}>
                  <i className="fas fa-info-circle" style={{marginRight: '0.5rem'}}></i>
                  A callback was already initiated for this room. If you didn't receive the call, you can request another one.
                </div>
              )}
              
              {callbackError && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {callbackError}
                </div>
              )}
              
              <div className="modal-footer">
                <p className="phone-update-hint">
                  Wrong number? <a href="/settings">Update your phone number</a>
                </p>
              </div>
            </>
          ) : (
            <div className="callback-success">
              <i className="fas fa-check-circle"></i>
              <h3>Callback Initiated!</h3>
              <p>You should receive a call at {formatPhoneDisplay(phoneNumber)} shortly.</p>
              <p className="success-hint">Please answer the call to join the room.</p>
              <button 
                className="close-success-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoomJoinModal;